"use client"

import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SofiaMark,
} from "@coval/ui"
import {
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from "lucide-react"

import type { CoviThreadMeta } from "../model/types"
import { ThreadRenameInput } from "./ThreadRenameInput"

/** The sidebar's icon column, matched to `SofiaSidebarNav`'s own row icons. */
const LEADING_ICON = "h-4 w-4 shrink-0 text-coval-text"

/**
 * One chat in a `CoviThreadList`: the row that opens it, its overflow menu, and
 * the inline rename and delete-confirm states that replace the row while either
 * is in progress. The list owns which row is in which state — at most one row
 * is ever mid-rename or mid-delete — so this component only draws what it is
 * told and reports back what was pressed.
 */
export function ThreadRow({
  thread,
  inset,
  showIcon,
  active,
  streaming,
  renaming,
  confirmingDelete,
  onSelect,
  onStartRename,
  onRename,
  onTogglePin,
  onCancelRename,
  onStartDelete,
  onConfirmDelete,
  onCancelDelete,
}: {
  thread: CoviThreadMeta
  /** Horizontal padding for the row's own content, set by the variant. */
  inset: string
  /** Whether the row leads with the sidebar's icon column. */
  showIcon: boolean
  active: boolean
  streaming: boolean
  renaming: boolean
  confirmingDelete: boolean
  onSelect: () => void
  onStartRename: () => void
  onRename: (title: string) => void
  onTogglePin: () => void
  onCancelRename: () => void
  onStartDelete: () => void
  onConfirmDelete: () => void
  onCancelDelete: () => void
}) {
  if (renaming) {
    return (
      <div className={cn("flex items-center gap-1 rounded-sm py-1", inset)}>
        <ThreadRenameInput
          title={thread.title}
          onCommit={onRename}
          onCancel={onCancelRename}
          className="text-sm"
        />
      </div>
    )
  }

  // Deleting a thread destroys its transcript, so it asks first — inline, so the
  // answer is next to the row it applies to rather than in a modal.
  if (confirmingDelete) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-sm bg-surface-2 py-1",
          inset,
        )}
      >
        <span className="min-w-0 flex-1 truncate text-sm text-coval-text">
          Delete “{thread.title}”?
        </span>
        <button
          type="button"
          onClick={onConfirmDelete}
          className="shrink-0 rounded px-1.5 py-0.5 text-sm font-medium text-coval-danger-text transition-colors hover:bg-surface-0"
        >
          Delete
        </button>
        <button
          type="button"
          onClick={onCancelDelete}
          className="shrink-0 rounded px-1.5 py-0.5 text-sm text-coval-text-muted transition-colors hover:bg-surface-0 hover:text-coval-text"
        >
          Cancel
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        // Row metrics track SidebarNavItemRow: a selected chat and a selected
        // app-sidebar destination are the same size and shape.
        "group/thread flex items-center gap-1 rounded-sm pr-1 transition-colors",
        active ? "bg-surface-1" : "hover:bg-surface-1",
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? "true" : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2 py-1 text-left",
          inset,
        )}
      >
        {showIcon ? (
          // A pinned chat spends the icon column on its pin rather than adding
          // a second marker beside the chat glyph: two icons would push every
          // pinned title off the margin the rest of the nav sits on.
          thread.pinned ? (
            <Pin aria-label="Pinned" className={LEADING_ICON} />
          ) : (
            <MessageSquare aria-hidden className={LEADING_ICON} />
          )
        ) : thread.pinned ? (
          <Pin
            aria-label="Pinned"
            className="h-3.5 w-3.5 shrink-0 text-coval-text"
          />
        ) : null}
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm text-coval-text">
              {thread.title}
            </span>
            {streaming ? (
              <span
                role="status"
                aria-label="Sofia is working"
                className="flex h-4 w-4 shrink-0 items-center justify-center text-coval-text-muted"
              >
                {/* The mark is inline-flex, so a non-flex wrapper would sit it
                    on the row's text baseline and ride high in the box. */}
                <span aria-hidden="true" className="flex">
                  <SofiaMark animated size={16} />
                </span>
              </span>
            ) : thread.unread ? (
              <span
                role="img"
                aria-label="Unread update"
                className="h-2 w-2 shrink-0 rounded-full bg-coval-info-border"
              />
            ) : null}
          </span>
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Thread options: ${thread.title}`}
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded text-coval-text-muted",
              "transition-colors hover:bg-surface-0 hover:text-coval-text",
              "opacity-0 focus-visible:opacity-100 group-hover/thread:opacity-100",
              active && "opacity-100",
            )}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem className="gap-2" onSelect={onStartRename}>
            <Pencil className="h-3.5 w-3.5" />
            <span>Rename</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2" onSelect={onTogglePin}>
            {thread.pinned ? (
              <PinOff className="h-3.5 w-3.5" />
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
            <span>{thread.pinned ? "Unpin" : "Pin"}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2"
            destructive
            onSelect={onStartDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
